import React, { useEffect, useState } from "react";
import { formatDate } from "../../helpers/dates";
import {
  GRANT_KINDS,
  farmPeriod,
  fetchGrants,
  kindTitle,
} from "../../helpers/grantsApi";

/**
 * «Мои награды» — витрина наград, полученных в мирах.
 *
 * Собрано по описанию Максима 22.08.2026: как в FairyTON — карточки с
 * разбивкой по видам и по мирам, а по нажатию всплывает карточка награды
 * с картинкой, названием, описанием, свойствами, ценой, названием монеты,
 * признаком AR-модели и миром, где она создана.
 *
 * Скриншотов FairyTON я так и не получил, поэтому собрано по тексту
 * задания. Расхождения в мелочах вероятны — поправим, когда покажет.
 *
 * Фильтры показываются только по тем видам, которые у человека есть:
 * пустая вкладка «Билеты» у того, кто билетов не покупал, — обещание,
 * которое некому выполнить.
 */

const AR_HINT = "AR 3D — награду можно рассмотреть в дополненной реальности";

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const CubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 3 7v10l9 5 9-5V7z" />
    <path d="M3 7l9 5 9-5M12 12v10" />
  </svg>
);

/** Цена с названием монеты: «120 FDP». Без валюты цену не показываем. */
const priceLine = (grant) => {
  if (!grant.price || !grant.currency) return null;
  return `${grant.price.toLocaleString("ru-RU")} ${grant.currency}`;
};

const GrantCard = ({ grant, worldName, onOpen }) => (
  <button type="button" className="fw-card fw-grant" onClick={() => onOpen(grant)}>
    <span className="fw-grant-pic">
      {grant.image ? (
        <img src={grant.image} alt="" />
      ) : (
        <span className="fw-grant-letter">{(grant.title || "?").slice(0, 1)}</span>
      )}

      {grant.count > 1 ? <span className="fw-grant-count">×{grant.count}</span> : null}
      {grant.has_ar ? (
        <span className="fw-grant-ar" title={AR_HINT}>
          <CubeIcon />
        </span>
      ) : null}
    </span>

    <span className="fw-grant-body">
      <b>{grant.title}</b>
      <span className="fw-grant-kind">{kindTitle(grant.kind)}</span>
      {worldName ? <span className="fw-grant-world">{worldName}</span> : null}
    </span>
  </button>
);

const GrantModal = ({ grant, worldName, onClose }) => {
  // Закрытие по Esc: окно во весь экран без выхода по клавише раздражает.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const price = priceLine(grant);
  const period = grant.farm ? farmPeriod(grant.farm.period) : null;

  return (
    <div className="fw-modal-back" onClick={onClose} role="presentation">
      <div
        className="fw-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={grant.title}
      >
        <button type="button" className="fw-modal-close" onClick={onClose} aria-label="Закрыть">
          <CloseIcon />
        </button>

        <div className="fw-modal-pic">
          {grant.image ? (
            <img src={grant.image} alt="" />
          ) : (
            <span className="fw-grant-letter big">{(grant.title || "?").slice(0, 1)}</span>
          )}
        </div>

        <div className="fw-modal-body">
          <span className="fw-chip">{kindTitle(grant.kind)}</span>
          <h3>{grant.title}</h3>

          {grant.description ? <p className="fw-modal-desc">{grant.description}</p> : null}

          <dl className="fw-modal-props">
            {grant.count > 1 ? (
              <>
                <dt>Количество</dt>
                <dd>{grant.count} шт.</dd>
              </>
            ) : null}

            {price ? (
              <>
                <dt>Цена</dt>
                <dd>{price}</dd>
              </>
            ) : null}

            {grant.farm ? (
              <>
                <dt>Приносит</dt>
                <dd>
                  {grant.farm.grant} {grant.farm.currency}
                  {period ? `, ${period}` : ""}
                  {grant.farm.rounds ? ` · кругов: ${grant.farm.rounds}` : ""}
                </dd>
              </>
            ) : null}

            {/* Свойства билета: срок и отметка о входе. Без них карточка
                билета — одно слово «Билет», по которому нельзя понять
                главного: не сгорел ли он и не использован ли уже. */}
            {grant.finish_at ? (
              <>
                <dt>Действует до</dt>
                <dd>{formatDate(grant.finish_at)}</dd>
              </>
            ) : null}

            {grant.activated_at ? (
              <>
                <dt>Использован</dt>
                <dd>{formatDate(grant.activated_at)}</dd>
              </>
            ) : null}

            {grant.has_ar ? (
              <>
                <dt>AR 3D</dt>
                <dd>Есть — награду можно рассмотреть объёмной</dd>
              </>
            ) : null}

            {worldName ? (
              <>
                <dt>Мир</dt>
                <dd>{worldName}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>
    </div>
  );
};

const GrantsBoard = () => {
  const [grants, setGrants] = useState([]);
  const [worlds, setWorlds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [kind, setKind] = useState(null);
  const [world, setWorld] = useState(null);
  const [opened, setOpened] = useState(null);

  useEffect(() => {
    let alive = true;

    fetchGrants()
      .then((data) => {
        if (!alive) return;
        setGrants((data && data.grants) || []);
        setWorlds((data && data.worlds) || []);
      })
      .catch((e) => {
        if (alive) setError(e && e.message ? e.message : "не удалось загрузить");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const worldName = (id) => {
    const found = worlds.find((w) => w.id === id);
    return found ? found.name : null;
  };

  // Показываем только те виды и миры, которые действительно есть.
  const presentKinds = GRANT_KINDS.filter((k) => grants.some((g) => g.kind === k.key));
  const presentWorlds = worlds.filter((w) => grants.some((g) => g.company_id === w.id));

  const shown = grants.filter(
    (g) => (!kind || g.kind === kind) && (!world || g.company_id === world)
  );

  if (loading) {
    return <p className="fw-sub">Загружаем награды…</p>;
  }

  if (error) {
    return (
      <section className="fw-card fw-auth-note">
        Не удалось загрузить награды: {error}. Обновите страницу; если не
        поможет, напишите в поддержку.
      </section>
    );
  }

  if (grants.length === 0) {
    return (
      <section className="fw-card fw-empty">
        <b>Наград пока нет</b>
        <p>
          Здесь появятся награды из миров, которые вы пройдёте: цифровые,
          коллекционные, фарм-награды и билеты. Начните с заданий первого
          уровня на главной.
        </p>
      </section>
    );
  }

  return (
    <>
      {presentKinds.length > 1 ? (
        <div className="fw-worlds-filter" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={kind === null}
            className={`fw-ptab${kind === null ? " active" : ""}`}
            onClick={() => setKind(null)}
          >
            Все виды
          </button>
          {presentKinds.map((k) => (
            <button
              key={k.key}
              type="button"
              role="tab"
              aria-selected={kind === k.key}
              className={`fw-ptab${kind === k.key ? " active" : ""}`}
              onClick={() => setKind(k.key)}
            >
              {k.title}
            </button>
          ))}
        </div>
      ) : null}

      {presentWorlds.length > 1 ? (
        <div className="fw-worlds-filter fw-grants-worlds" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={world === null}
            className={`fw-ptab${world === null ? " active" : ""}`}
            onClick={() => setWorld(null)}
          >
            Все миры
          </button>
          {presentWorlds.map((w) => (
            <button
              key={w.id}
              type="button"
              role="tab"
              aria-selected={world === w.id}
              className={`fw-ptab${world === w.id ? " active" : ""}`}
              onClick={() => setWorld(w.id)}
            >
              {w.name}
            </button>
          ))}
        </div>
      ) : null}

      <div className="fw-grants">
        {shown.map((g) => (
          <GrantCard
            key={g.key}
            grant={g}
            worldName={worldName(g.company_id)}
            onOpen={setOpened}
          />
        ))}
      </div>

      {opened ? (
        <GrantModal
          grant={opened}
          worldName={worldName(opened.company_id)}
          onClose={() => setOpened(null)}
        />
      ) : null}
    </>
  );
};

export default GrantsBoard;
